/**
 * Подарунок за промокодом — попап поверх головної (owner 2026-08-28).
 *
 * Було: код відкривав окремий екран промо. Owner: «хай відкривається головна,
 * а поверх неї наш звичайний попап — з анімованим емодзі, красиво: тримай
 * подарунок, активуй».
 *
 * Каркас — той самий, що в решти попапів: центрована картка, хрестик, кнопки
 * колонкою через `popupButtons`. Емодзі анімований (правило проєкту: емодзі
 * над заголовком попапа — тільки Lottie з `LOTTIE_CPS`).
 */
import { createPortal } from "react-dom";

import { PopupCloseButton } from "@/components/PopupClose";
import { TgEmoji } from "@/components/TgEmoji";
import { usePromoActivate, usePromoCheck } from "@/api/hooks";
import { showAppAlert } from "@/lib/appAlert";
import { popupActionsCol, popupBtnPrimary } from "@/lib/popupButtons";
import { tg } from "@/lib/telegram";
import { cn } from "@/lib/utils";

function daysWord(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 14) return "днів";
  const last = n % 10;
  if (last === 1) return "день";
  if (last >= 2 && last <= 4) return "дні";
  return "днів";
}

export function PromoGiftModal({
  slug,
  onClose,
  preview = false,
}: {
  slug: string;
  onClose: () => void;
  /** Галерея попапів: показати вигляд без запитів і оплати. */
  preview?: boolean;
}) {
  const check = usePromoCheck(slug, !preview && slug.length > 0);
  const activate = usePromoActivate();

  // Попап зʼявляється ЦІЛИМ (owner 2026-08-28): поки не приїхали дні й ціна,
  // не показуємо нічого. Інакше спершу малювалась порожня картка, а плитка з
  // подарунком доїжджала окремо — виглядало як підвантаження шматками.
  if (!preview && (check.isLoading || !check.data)) return null;

  const days = preview ? 14 : check.data?.days ?? 0;
  const amount = preview ? "1" : check.data?.amount ?? "1";
  const available = preview ? true : check.data?.available ?? true;

  const handleActivate = async () => {
    if (preview) return;
    const webapp = tg();
    try {
      const result = await activate.mutateAsync(slug);
      localStorage.setItem(
        "pending_invoice",
        JSON.stringify({
          order_reference: result.order_reference,
          tariff_slug: `promo:${slug}`,
          started_at_before: Date.now(),
        }),
      );
      if (webapp?.openLink) webapp.openLink(result.purchase_url);
      else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Не вдалося активувати: ${(e as Error).message}`);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />

        <div className="mb-4 text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🎁" size={52} />
          </div>
          <div className="mb-1.5 font-display text-[1.1875rem] font-bold leading-tight text-ink [text-wrap:balance]">
            {available ? "Це твій подарунок" : "Промокод уже використано"}
          </div>
          <div className="text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
            {available
              ? "Тримай повний доступ до inFLOW — вакансії приходять миттєво, контакти відкриваються без лімітів."
              : "Ти вже активував його раніше. Але підписку можна оформити у розділі «Підписка»."}
          </div>
        </div>

        {available && days > 0 && (
          <div className="mb-4 rounded-2xl bg-primary/[0.06] p-4 text-center ring-1 ring-primary/20">
            <div className="mb-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim">
              Твій подарунок
            </div>
            <div className="font-display text-[1.5rem] font-bold leading-none text-primary">
              {days} {daysWord(days)} за {amount} ₴
            </div>
            <div className="mt-1 text-[0.6875rem] text-ink-dim">
              Замість повної вартості підписки
            </div>
          </div>
        )}

        <div className={popupActionsCol}>
          {available && (
            <button
              type="button"
              onClick={handleActivate}
              disabled={activate.isPending}
              className={cn(popupBtnPrimary, "disabled:opacity-50")}
            >
              {activate.isPending ? "Відкриваю оплату…" : `Активувати за ${amount} ₴`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
